import React, { useState, useEffect } from 'react'
import { useTheme } from '../../context/ThemeContext'
import { useScrollPosition } from '../../hooks/useScrollAnimation'
import { SunIcon, MoonIcon, MenuIcon, CloseIcon } from '../../assets/icons'
import { landingContent } from '../../config/content.config'
import { getLinkProps, isExternalLink } from '../../utils/navigation'
import Button from '../Button/Button'
import './Header.css'
import CLLogoWhite from 'src/assets/new-landing/cl_logo_white.webp'
import CLLogoColor from 'src/assets/new-landing/cl_logo_color.webp'

const Header = () => {
  const { isDark, toggleTheme } = useTheme()
  const { scrollPosition } = useScrollPosition()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const { brand, header } = landingContent

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  const isScrolled = scrollPosition > 50

  const navLinks = header.navLinks.map(link => ({
    ...link,
    external: isExternalLink(link.href)
  }))

  const headerButtons = header.buttons.map(button => ({
    ...button,
    external: isExternalLink(button.href)
  }))

  const handleNavClick = (e, link) => {
    if (link.external) {
      // External links: let default behavior handle it (opens in new tab via getLinkProps)
      setIsMobileMenuOpen(false)
      return
    }

    // Internal links: smooth scroll
    e.preventDefault()
    if (link.href && link.href.startsWith('#')) {
      const element = document.querySelector(link.href)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
    setIsMobileMenuOpen(false)
  }

  return (
    <>
      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="header-inner">
          <a href="#home" className="logo" onClick={(e) => handleNavClick(e, { href: '#home', external: false })}>
            <img
              src={isScrolled || isDark ? CLLogoWhite : CLLogoColor}
              alt={brand.name}
              className="logo-img"
            />
          </a>

          <nav className="nav">
            <ul className="nav-links">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="nav-link"
                    onClick={(e) => handleNavClick(e, link)}
                    {...getLinkProps(link)}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>

            <div className="header-actions">
              {/* Theme toggle - hidden on mobile */}
              <button
                className="theme-toggle"
                onClick={toggleTheme}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <SunIcon /> : <MoonIcon />}
              </button>

              {headerButtons.map((button, index) => (
                <Button
                  key={index}
                  href={button.href}
                  external={button.external}
                  sameWindow={button.sameWindow}
                  className={button.className}
                  variant={button.variant}
                >
                  {button.label}
                </Button>
              ))}

              <button
                className="mobile-menu-btn"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <ul className="mobile-nav-links">
          {navLinks.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                className="mobile-nav-link"
                onClick={(e) => handleNavClick(e, link)}
                {...getLinkProps(link)}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        {/* Mobile theme toggle */}
        <button
          className="mobile-theme-toggle"
          onClick={toggleTheme}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <SunIcon /> : <MoonIcon />}
        </button>

        <div className="mobile-buttons">
          {headerButtons.map((button, index) => (
            <Button
              key={index}
              href={button.href}
              external={button.external}
              sameWindow={button.sameWindow}
              variant={button.variant}
            >
              {button.label}
            </Button>
          ))}
        </div>
      </div>
    </>
  )
}

export default Header
